import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Phone, Mail, MessageSquare, Shield, Bell, Check, XCircle, Clock, KeyRound, Building2, Droplets, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userAPI, notificationSettingsAPI, authAPI, preferencesAPI, notificationPrefsAPI, settingsAPI } from '../api';
import { requestPermission, getPermission } from '../lib/notificationService';
import { ClientSectorMaster } from '../components/ClientSectorMaster';
import { SheetSourceMaster } from '../components/SheetSourceMaster';

export const SettingsPage = () => {
  const { user, checkAuth, updateUser, hasPermission } = useAuth();
  const canManageSectors = user?.role === 'super_admin' || hasPermission?.('leads.sector.manage');
  const canManageSheets = user?.role === 'super_admin' || hasPermission?.('import.sheets.manage');
  const [activeTab, setActiveTab] = useState('notifications');
  const isSuperAdmin = user?.role === 'super_admin';

  const [userNotif, setUserNotif] = useState(user?.notifications_enabled !== false);
  const [globalNotif, setGlobalNotif] = useState(true);
  const [globalLoaded, setGlobalLoaded] = useState(false);
  const [permission, setPermission] = useState(getPermission());
  const [saving, setSaving] = useState(false);

  // Change password
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  // Time format preference
  const [timeFormat, setTimeFormat] = useState(user?.time_format || '12h');
  // Batch 3: reminder delivery preferences
  const [reminderPrefs, setReminderPrefs] = useState(null);
  const [reminderProviders, setReminderProviders] = useState({ email: false, whatsapp: false });
  const [reminderEvents, setReminderEvents] = useState([]);
  // Batch 3 closure: company profile (receipt branding)
  const [company, setCompany] = useState({ name: '', logo_url: '', address: '', gstin: '', phone: '', email: '', website: '' });
  // B5D.1 Leak SLA (view: settings.view, edit: settings.manage - enforced by the backend)
  const [sla, setSla] = useState(null);
  const [slaSaving, setSlaSaving] = useState(false);

  const loadSla = () => settingsAPI.getLeakSla().then(r => setSla(r.data)).catch(() => setSla(null));

  useEffect(() => {
    if (isSuperAdmin) settingsAPI.getCompany().then(r => setCompany(c => ({ ...c, ...r.data }))).catch(() => {});
    loadSla();
  }, [isSuperAdmin]);

  const saveSla = async () => {
    setSlaSaving(true);
    try {
      const r = await settingsAPI.updateLeakSla(sla.values);
      toast.success(Object.keys(r.data.changed || {}).length
        ? 'Leak SLA updated — leak status recalculates on the next load'
        : 'Leak SLA saved (no change)');
      loadSla();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save Leak SLA'); }
    setSlaSaving(false);
  };

  const resetSla = async () => {
    setSlaSaving(true);
    try { await settingsAPI.resetLeakSla(); toast.success('Leak SLA restored to approved defaults'); loadSla(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset Leak SLA'); }
    setSlaSaving(false);
  };

  const saveCompany = async () => {
    try { await settingsAPI.updateCompany(company); toast.success('Company profile saved — used on receipts'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save company profile'); }
  };

  useEffect(() => {
    notificationPrefsAPI.get().then(r => {
      setReminderPrefs(r.data.preferences);
      setReminderProviders(r.data.providers || {});
      setReminderEvents(r.data.events || []);
    }).catch(() => {});
  }, []);

  const saveReminderPrefs = async () => {
    try { await notificationPrefsAPI.set(reminderPrefs); toast.success('Reminder preferences saved'); }
    catch (e) { toast.error('Failed to save preferences'); }
  };

  useEffect(() => {
    setTimeFormat(user?.time_format || '12h');
  }, [user]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('New passwords do not match'); return; }
    setPwdSaving(true);
    try {
      await authAPI.changePassword(pwd.current, pwd.next);
      setPwd({ current: '', next: '', confirm: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  };

  const handleSaveTimeFormat = async (value) => {
    setSaving(true);
    try {
      await preferencesAPI.setTimeFormat(value);
      setTimeFormat(value);
      updateUser({ time_format: value });
      toast.success(`Time format set to ${value === '24h' ? '24-hour' : '12-hour AM/PM'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update time format');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setUserNotif(user?.notifications_enabled !== false);
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await notificationSettingsAPI.getGlobal();
        setGlobalNotif(data.enabled !== false);
      } catch {
        // ignore
      } finally {
        setGlobalLoaded(true);
      }
    })();
  }, []);

  const handleEnableBrowser = async () => {
    const result = await requestPermission();
    setPermission(result);
    if (result === 'granted') {
      toast.success('Browser notifications enabled');
      // Fire a welcome ping
      try {
        new Notification('GNONS CRM', { body: 'Notifications are now on. You\'ll be alerted when follow-ups are due.' });
      } catch { /* ignore */ }
    } else if (result === 'denied') {
      toast.error('Browser blocked notifications. Enable in browser settings.');
    }
  };

  const handleSaveUserNotif = async (nextValue) => {
    setSaving(true);
    try {
      await userAPI.updateMyNotifications(nextValue);
      setUserNotif(nextValue);
      await checkAuth(); // refresh user object
      toast.success(`Follow-up notifications ${nextValue ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGlobalNotif = async (nextValue) => {
    setSaving(true);
    try {
      await notificationSettingsAPI.setGlobal(nextValue);
      setGlobalNotif(nextValue);
      toast.success(`Global notifications ${nextValue ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update global setting');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    toast.success('Settings saved successfully');
  };

  const tabs = [
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'reminders', label: 'Reminder Delivery', icon: Bell },
    { id: 'preferences', label: 'Preferences', icon: Clock },
    ...(isSuperAdmin ? [{ id: 'company', label: 'Company Profile', icon: Building2 }] : []),
    ...(sla ? [{ id: 'leak-sla', label: 'Leak SLA', icon: Droplets }] : []),
    ...(canManageSectors ? [{ id: 'sectors', label: 'Client Sectors', icon: Building2 }] : []),
    ...(canManageSheets ? [{ id: 'sheet-sources', label: 'Google Sheet Sources', icon: Building2 }] : []),
    { id: 'communication', label: 'Communication', icon: Phone },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { id: 'security', label: 'Security', icon: Shield }
  ];

  const Toggle = ({ checked, onChange, disabled, testId }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={testId}
        className="sr-only peer"
      />
      <div className={`w-11 h-6 rounded-full transition-colors peer ${checked ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'} ${disabled ? 'opacity-50' : ''}`}>
        <div className={`absolute top-[2px] left-[2px] bg-white rounded-full h-5 w-5 transition-transform ${checked ? 'translate-x-5' : ''}`}></div>
      </div>
    </label>
  );

  return (
    <div className="space-y-6" data-testid="settings-container">
      <div>
        <h1 className="text-4xl font-outfit font-semibold">Settings</h1>
        <p className="text-muted-foreground mt-2">Configure your CRM preferences and integrations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="clay-card p-4 h-fit">
          <div className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`settings-tab-${tab.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeTab === tab.id ? 'bg-primary text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 clay-card p-6">
          {activeTab === 'sectors' && canManageSectors && <ClientSectorMaster />}
          {activeTab === 'sheet-sources' && canManageSheets && <SheetSourceMaster />}
          {activeTab === 'notifications' && (
            <div className="space-y-8" data-testid="settings-notifications-tab">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-2">Follow-up Browser Notifications</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Show a browser notification when a follow-up reminder is due, even if the CRM tab is in the background.
                </p>

                {/* Browser Permission */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Browser Permission</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {permission === 'granted' && <span className="inline-flex items-center gap-1 text-green-600"><Check className="h-3 w-3" /> Granted</span>}
                        {permission === 'denied' && <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="h-3 w-3" /> Blocked (enable in your browser settings)</span>}
                        {permission === 'default' && <span className="text-yellow-600">Not requested yet</span>}
                      </p>
                    </div>
                    {permission !== 'granted' && (
                      <button
                        onClick={handleEnableBrowser}
                        data-testid="settings-enable-browser-notif-btn"
                        className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg"
                      >
                        {permission === 'denied' ? 'Blocked' : 'Enable in Browser'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Per-User Setting */}
                <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
                  <div>
                    <p className="text-sm font-medium">My follow-up alerts</p>
                    <p className="text-xs text-muted-foreground mt-1">Enable browser notifications for my scheduled follow-ups.</p>
                  </div>
                  <Toggle
                    checked={userNotif}
                    onChange={handleSaveUserNotif}
                    disabled={saving}
                    testId="settings-user-notif-toggle"
                  />
                </div>

                {/* Global Setting (Super Admin) */}
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium">Global notifications (Super Admin)</p>
                    <p className="text-xs text-muted-foreground mt-1">Enable browser notifications system-wide for all users.</p>
                  </div>
                  <Toggle
                    checked={globalNotif}
                    onChange={handleSaveGlobalNotif}
                    disabled={!isSuperAdmin || !globalLoaded || saving}
                    testId="settings-global-notif-toggle"
                  />
                </div>
                {!isSuperAdmin && (
                  <p className="text-xs text-muted-foreground italic mt-2">Only Super Admin can change the global setting.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'reminders' && (
            <div className="space-y-6" data-testid="settings-reminders-tab">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-1">Reminder Delivery Channels</h2>
                <p className="text-sm text-muted-foreground mb-4">Choose how you receive each alert. In-CRM always available. Email/WhatsApp require a provider (currently {reminderProviders.email || reminderProviders.whatsapp ? 'partially configured' : 'not configured — those channels stay disabled'}).</p>
                {!reminderPrefs ? <p className="text-sm text-muted-foreground">Loading…</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-muted-foreground text-xs"><tr>
                        <th className="py-2">Event</th><th className="text-center">In-CRM</th>
                        <th className="text-center">Email{!reminderProviders.email && ' (disabled)'}</th>
                        <th className="text-center">WhatsApp{!reminderProviders.whatsapp && ' (disabled)'}</th>
                      </tr></thead>
                      <tbody>
                        {reminderEvents.map((ev) => (
                          <tr key={ev} className="border-t border-slate-100 dark:border-slate-800" data-testid={`reminder-row-${ev}`}>
                            <td className="py-2 capitalize">{ev.replace(/_/g, ' ')}</td>
                            {['in_app', 'email', 'whatsapp'].map((ch) => (
                              <td key={ch} className="text-center">
                                <Toggle
                                  checked={!!reminderPrefs[ev]?.[ch]}
                                  disabled={ch !== 'in_app' && !reminderProviders[ch]}
                                  testId={`reminder-${ev}-${ch}`}
                                  onChange={(val) => setReminderPrefs(p => ({ ...p, [ev]: { ...p[ev], [ch]: val } }))}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <button onClick={saveReminderPrefs} data-testid="reminder-save" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm"><Save className="h-4 w-4" /> Save Preferences</button>
              </div>
            </div>
          )}

          {activeTab === 'leak-sla' && sla && (
            <div className="space-y-6" data-testid="settings-leak-sla-tab">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-1">Leak SLA Thresholds</h2>
                <p className="text-sm text-muted-foreground">
                  Leak status is calculated when leads are read, so changing a threshold re-evaluates every
                  lead instantly and never edits lead records or their timeline.
                </p>
                {!sla.can_edit && (
                  <p className="mt-2 text-sm text-amber-600" data-testid="sla-readonly-note">
                    You can view these thresholds. Only a Super Admin can change them.
                  </p>
                )}
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {sla.fields.map((f) => (
                    <div key={f.key}>
                      <label className="block text-xs text-muted-foreground mb-1">{f.label} ({f.unit})</label>
                      <input type="number" min={f.min} max={f.max} disabled={!sla.can_edit || slaSaving}
                        data-testid={`sla-${f.key}`} value={sla.values[f.key] ?? ''}
                        onChange={(e) => setSla({ ...sla, values: { ...sla.values, [f.key]: e.target.value } })}
                        className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm disabled:opacity-60" />
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Allowed {f.min}–{f.max} · default {f.default}
                      </p>
                    </div>
                  ))}
                </div>
                {sla.updated_at && (
                  <p className="mt-3 text-xs text-muted-foreground" data-testid="sla-last-changed">
                    Last changed {new Date(sla.updated_at).toLocaleString('en-IN')}{sla.updated_by ? ` by ${sla.updated_by}` : ''}
                  </p>
                )}
                {sla.can_edit && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button onClick={saveSla} disabled={slaSaving} data-testid="sla-save"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-60">
                      <Save className="h-4 w-4" /> Save Leak SLA
                    </button>
                    <button onClick={resetSla} disabled={slaSaving} data-testid="sla-reset"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-60">
                      <RotateCcw className="h-4 w-4" /> Restore defaults
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'company' && isSuperAdmin && (
            <div className="space-y-6" data-testid="settings-company-tab">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-1">Company Profile</h2>
                <p className="text-sm text-muted-foreground mb-4">Used to brand generated Payment Receipts.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[['name', 'Company Name'], ['gstin', 'GSTIN'], ['phone', 'Phone'], ['email', 'Email'], ['website', 'Website'], ['logo_url', 'Logo URL']].map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-xs font-medium mb-1">{label}</label>
                      <input value={company[k] || ''} onChange={(e) => setCompany({ ...company, [k]: e.target.value })}
                        data-testid={`company-${k}`} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium mb-1">Address</label>
                    <textarea value={company.address || ''} onChange={(e) => setCompany({ ...company, address: e.target.value })}
                      data-testid="company-address" rows={2} className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
                  </div>
                </div>
                {company.logo_url ? <img src={company.logo_url} alt="logo" className="mt-3 h-12 object-contain" onError={(e) => { e.target.style.display = 'none'; }} /> : null}
                <button onClick={saveCompany} data-testid="company-save" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm"><Save className="h-4 w-4" /> Save Company Profile</button>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6" data-testid="settings-preferences-tab">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-2">Time Format</h2>
                <p className="text-sm text-muted-foreground mb-4">Choose how timestamps are displayed across the CRM (reporting timezone: Asia/Kolkata).</p>
                <div className="flex gap-3">
                  {[{ v: '12h', label: '12-hour (AM/PM)' }, { v: '24h', label: '24-hour' }].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => handleSaveTimeFormat(opt.v)}
                      disabled={saving}
                      data-testid={`settings-timeformat-${opt.v}`}
                      className={`px-5 py-3 rounded-lg border text-sm transition-colors ${timeFormat === opt.v ? 'bg-primary text-white border-primary' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'communication' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-2">Twilio Configuration</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure Twilio for Click-to-Call and SMS. Get credentials from{' '}
                  <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">Twilio Console</a>
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Twilio Account SID</label>
                    <input type="text" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxx" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Twilio Auth Token</label>
                    <input type="password" placeholder="Your auth token" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Twilio Phone Number</label>
                    <input type="text" placeholder="+1234567890" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                </div>
              </div>
              <button onClick={handleSave} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg">
                <Save className="h-4 w-4" />
                Save Configuration
              </button>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-2">Email Configuration</h2>
                <p className="text-sm text-muted-foreground mb-4">Choose and configure your email provider</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Provider</label>
                    <select className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900">
                      <option value="sendgrid">SendGrid</option>
                      <option value="resend">Resend</option>
                      <option value="gmail">Gmail SMTP</option>
                      <option value="outlook">Microsoft Outlook SMTP</option>
                      <option value="custom">Custom SMTP</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">API Key</label>
                    <input type="password" placeholder="Your API key" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Verified Sender Email</label>
                    <input type="email" placeholder="sales@gnons.com" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Sender Name</label>
                    <input type="text" placeholder="GNONS Design & Construction" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                </div>
              </div>
              <button onClick={handleSave} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg">
                <Save className="h-4 w-4" />
                Save Configuration
              </button>
            </div>
          )}

          {activeTab === 'whatsapp' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-2">WhatsApp Business API</h2>
                <p className="text-sm text-muted-foreground mb-4">Configure Meta WhatsApp Business Cloud API</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Meta Business Account ID</label>
                    <input type="text" placeholder="Your business account ID" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">WhatsApp Phone Number ID</label>
                    <input type="text" placeholder="Your phone number ID" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Permanent Access Token</label>
                    <input type="password" placeholder="Your permanent access token" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Webhook Verify Token</label>
                    <input type="text" placeholder="Custom verify token" className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                </div>
              </div>
              <button onClick={handleSave} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg">
                <Save className="h-4 w-4" />
                Save Configuration
              </button>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-8" data-testid="settings-security-tab">
              <div>
                <h2 className="text-xl font-outfit font-medium mb-2 flex items-center gap-2"><KeyRound className="h-5 w-5" /> Change Password</h2>
                <p className="text-sm text-muted-foreground mb-4">Update your own password. You must enter your current password to confirm your identity.</p>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md" data-testid="change-password-form">
                  <div>
                    <label className="block text-sm font-medium mb-2">Current Password</label>
                    <input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                      data-testid="cp-current" autoComplete="current-password" required
                      className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">New Password</label>
                    <input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                      data-testid="cp-new" autoComplete="new-password" required
                      className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                    <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                      data-testid="cp-confirm" autoComplete="new-password" required
                      className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary/50 bg-white dark:bg-slate-900" />
                  </div>
                  <button type="submit" disabled={pwdSaving} data-testid="cp-submit"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg disabled:opacity-50">
                    <Save className="h-4 w-4" /> {pwdSaving ? 'Saving...' : 'Change Password'}
                  </button>
                </form>
              </div>

              <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <h2 className="text-xl font-outfit font-medium mb-2">Session</h2>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium">Session Timeout</p>
                    <p className="text-xs text-muted-foreground">Access tokens expire automatically for security.</p>
                  </div>
                  <span className="text-sm text-muted-foreground">24 hours (token) / 7 days (refresh)</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
